const SHOPIFY_API_VERSION = '2025-07';
export const SHOPIFY_STORE_PERMANENT_DOMAIN = 'brimmob-b0ty4.myshopify.com';
const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
const SHOPIFY_STOREFRONT_TOKEN = 'b4df64b0a80107d7d7de8394801f44e8';

import { toast } from 'sonner';

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
    images: { edges: Array<{ node: { url: string; altText: string | null } }> };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          price: { amount: string; currencyCode: string };
          availableForSale: boolean;
          selectedOptions: Array<{ name: string; value: string }>;
        };
      }>;
    };
    options: Array<{ name: string; values: string[] }>;
  };
}

export async function storefrontApiRequest(query: string, variables: Record<string, unknown> = {}) {
  const cacheKey = `${query}::${JSON.stringify(variables)}`;
  const isReadQuery = /^\s*query\b/.test(query);

  if (isReadQuery) {
    const cached = REQUEST_CACHE.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    const inFlight = INFLIGHT.get(cacheKey);
    if (inFlight) return inFlight;
  }

  const promise = doRequest(query, variables).then((data) => {
    if (isReadQuery && data) {
      REQUEST_CACHE.set(cacheKey, { data, timestamp: Date.now() });
    }
    INFLIGHT.delete(cacheKey);
    return data;
  }).catch((err) => {
    INFLIGHT.delete(cacheKey);
    throw err;
  });
  if (isReadQuery) INFLIGHT.set(cacheKey, promise);
  return promise;
}

const CACHE_TTL_MS = 60_000;
const REQUEST_CACHE = new Map<string, { data: unknown; timestamp: number }>();
const INFLIGHT = new Map<string, Promise<unknown>>();

async function doRequest(query: string, variables: Record<string, unknown>) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error('Shopify: Payment required', {
      description: 'Shopify API access requires an active billing plan. Visit https://admin.shopify.com to upgrade.',
    });
    return;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.errors) {
    throw new Error(`Error calling Shopify: ${data.errors.map((e: { message: string }) => e.message).join(', ')}`);
  }
  return data;
}

/**
 * Add Shopify CDN width parameter to deliver appropriately-sized images.
 * Falls through unchanged for non-Shopify URLs.
 */
export function shopifyImage(url: string | undefined | null, width = 600): string {
  if (!url) return "";
  if (!url.includes("cdn.shopify.com") && !url.includes("shopify.com")) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("width", String(width));
    return u.toString();
  } catch {
    return url;
  }
}

export const STOREFRONT_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          priceRange { minVariantPrice { amount currencyCode } }
          images(first: 5) { edges { node { url altText } } }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price { amount currencyCode }
                availableForSale
                selectedOptions { name value }
              }
            }
          }
          options { name values }
        }
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProduct($handle: String!) {
    product(handle: $handle) {
      id
      title
      description
      handle
      priceRange { minVariantPrice { amount currencyCode } }
      images(first: 10) { edges { node { url altText } } }
      variants(first: 20) {
        edges {
          node {
            id
            title
            price { amount currencyCode }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
      options { name values }
    }
  }
`;